import React, { useCallback, useEffect, useState } from 'react';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { requestJson } from '../utils/lifecycle';
import { describeApiError } from '../utils/format';

/** Search CRM companies and pick one. Calls `onSelect({ id, name, ... })`. */
export default function CompanyPicker({ selected, onSelect, id = 'company-search' }) {
  const { language } = useLanguage();
  const t = translations[language];
  const [query, setQuery] = useState('');
  const [state, setState] = useState({ items: [], loading: true, error: '' });

  const search = useCallback(async (text) => {
    setState((previous) => ({ ...previous, loading: true, error: '' }));
    try {
      const data = await requestJson(`/companies?q=${encodeURIComponent(text)}&page=1&size=10`);
      setState({ items: data.items || [], loading: false, error: '' });
    } catch (error) {
      if (error.status === 401) return;
      setState((previous) => ({ ...previous, loading: false, error: describeApiError(error, t, t.companiesFailed) }));
    }
  }, [t]);

  useEffect(() => { search(''); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Not a <form>: the picker sits inside the request wizard's form, and forms cannot nest.
  const runSearch = () => search(query.trim());
  const searchOnEnter = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    runSearch();
  };

  return <div className="stack">
    <div className="search" role="search">
      <MagnifyingGlass size={20} aria-hidden="true" />
      <input id={id} className="input" value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={searchOnEnter} placeholder={t.companySearch} aria-label={t.companySearch} />
      <button className="btn" type="button" onClick={runSearch}>{t.companySearchAction}</button>
    </div>
    {selected?.id && <p className="notice notice--ok" role="status">
      <span><span className="sr-only">{t.companySelected}: </span><strong>{selected.name}</strong></span>
      <small>{selected.id}</small>
    </p>}
    {state.loading && <p className="hint" role="status">{t.loading}</p>}
    {state.error && <p className="error-text" role="alert">{state.error}</p>}
    {!state.loading && !state.error && state.items.length === 0 && <p className="hint">{t.companyNone}</p>}
    <div className="row-list">
      {state.items.map((item) => <button className="option" key={item.id} type="button" onClick={() => onSelect(item)} aria-pressed={selected?.id === item.id}>
        <span className="option__mark" aria-hidden="true">&#10003;</span>
        <span className="option__body"><span className="option__title">{item.name}</span><span className="option__meta">{item.id}</span></span>
      </button>)}
    </div>
  </div>;
}
